'use client';

import { Box } from '@terra/ui-styled-system';
import { Cancel01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../../components/button';
import {
  MenuWithItems,
  type MenuContextApi,
  type MenuItemProps,
} from '../../components/styled/menu';
import { TagsInput } from '../../components/tags-input';
import { BodyDefault } from '../../components/typography';
import {
  BUSINESS_UNIT_INPUT_RESERVED_WIDTH_PX,
  BUSINESS_UNIT_TAG_MAX_WIDTH_PX,
  filterBusinessUnitsByQuery,
  isExistingBusinessUnitName,
  resolveBusinessUnitTagChange,
  toggleBusinessUnit,
  type BusinessUnitOption,
} from './business-unit-selector.model';
import {
  BusinessUnitOverflowChip,
  BusinessUnitTagMeasure,
} from './BusinessUnitTagChips';
import { useBusinessUnitTagOverflow } from './useBusinessUnitTagOverflow';

const MENU_CLASS_NAME = 'business-unit-selector-menu';
const ITEM_CLASS_NAME = 'business-unit-tags-input-item';

export type BusinessUnitSelectorProps = {
  value: readonly BusinessUnitOption[];
  placeholder: string;
  allBusinessUnits: readonly BusinessUnitOption[];
  onValueChange: (value: BusinessUnitOption[]) => void;
  onCreateBusinessUnit: (name: string) => Promise<BusinessUnitOption>;
  isCreatingBusinessUnit?: boolean;
  disabled?: boolean;
  height?: string;
  inputReservedWidthPx?: number;
};

export const BusinessUnitSelector = ({
  value,
  placeholder,
  allBusinessUnits,
  onValueChange,
  onCreateBusinessUnit,
  isCreatingBusinessUnit = false,
  disabled = false,
  height = '10',
  inputReservedWidthPx = BUSINESS_UNIT_INPUT_RESERVED_WIDTH_PX,
}: BusinessUnitSelectorProps) => {
  const t = useTranslations('designSystem.businessUnits');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuApiRef = useRef<MenuContextApi | null>(null);
  const isInteractionDisabled = disabled || isCreatingBusinessUnit;
  const { controlRef, measureRef, visibleTagCount } =
    useBusinessUnitTagOverflow({
      selectedCount: value.length,
      valueFingerprint: value.map(({ id }) => id).join(','),
      inputReservedWidthPx,
      remeasureKey: `${isMenuOpen}:${searchQuery}`,
    });

  useEffect(() => {
    const closeMenuHandler = (event: MouseEvent) => {
      const { target } = event;

      if (!(target instanceof Element)) {
        return;
      }

      const isInsideInput = rootRef.current?.contains(target) ?? false;
      const isInsideItem = Boolean(target.closest(`.${ITEM_CLASS_NAME}`));
      const isInsideMenu = Boolean(target.closest(`.${MENU_CLASS_NAME}`));

      if (!isInsideInput && !isInsideMenu && !isInsideItem) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('click', closeMenuHandler);

    return () => document.removeEventListener('click', closeMenuHandler);
  }, []);

  const visibleBusinessUnits = useMemo(
    () =>
      filterBusinessUnitsByQuery({
        businessUnits: allBusinessUnits,
        query: searchQuery,
      }),
    [allBusinessUnits, searchQuery],
  );
  const isExistingName = isExistingBusinessUnitName({
    query: searchQuery,
    businessUnits: [...allBusinessUnits, ...value],
  });
  const hiddenTagCount = Math.max(0, value.length - visibleTagCount);
  const hiddenBusinessUnitNames = value
    .slice(visibleTagCount)
    .map(({ name }) => name);
  const menuItems: MenuItemProps[] = visibleBusinessUnits.map(
    (businessUnit) => ({
      value: businessUnit.id,
      label: <BodyDefault>{businessUnit.name}</BodyDefault>,
      selected: value.some(({ id }) => id === businessUnit.id),
      onClick: () => {
        onValueChange(toggleBusinessUnit({ value, businessUnit }));
        menuApiRef.current?.reposition();
      },
    }),
  );

  const createMissingBusinessUnits = async ({
    names,
    selected,
  }: {
    names: readonly string[];
    selected: readonly BusinessUnitOption[];
  }) => {
    const results = await Promise.allSettled(
      names.map((name) => onCreateBusinessUnit(name)),
    );
    const created = results.flatMap((result) => {
      if (result.status === 'fulfilled') {
        return [{ id: result.value.id, name: result.value.name }];
      }

      console.error(
        'Failed to create business unit from tags input',
        result.reason instanceof Error
          ? result.reason
          : new Error(String(result.reason)),
      );

      return [];
    });

    if (created.length === 0) {
      return;
    }

    onValueChange([...selected, ...created]);
    setSearchQuery('');
    menuApiRef.current?.reposition();
  };

  const handleTagsValueChange = ({
    value: nextTagNames,
  }: {
    value: string[];
  }) => {
    const { selected, missingNames } = resolveBusinessUnitTagChange({
      nextTagNames,
      value,
      allBusinessUnits,
    });

    onValueChange(selected);

    if (missingNames.length === 0 || isInteractionDisabled) {
      return;
    }

    void createMissingBusinessUnits({ names: missingNames, selected });
  };

  return (
    <>
      <TagsInput.Root
        value={value.map(({ name }) => name)}
        onValueChange={handleTagsValueChange}
        onFocus={() => {
          setIsMenuOpen(true);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
        ref={rootRef}
        disabled={isInteractionDisabled}
      >
        <TagsInput.Control
          position="relative"
          display="flex"
          flexWrap="nowrap"
          overflow="hidden"
          alignItems="center"
          gap={1}
          h={height}
          minH={height}
          py={0}
          px={3}
        >
          <Box
            ref={controlRef}
            position="relative"
            display="flex"
            flexWrap="nowrap"
            alignItems="center"
            gap={1}
            flex="1"
            minW={0}
            h="full"
            overflow="hidden"
          >
            <Box
              ref={measureRef}
              position="absolute"
              top={0}
              left="-9999px"
              visibility="hidden"
              pointerEvents="none"
              aria-hidden
              display="flex"
              alignItems="center"
              gap={1}
              whiteSpace="nowrap"
            >
              {value.map(({ id, name }) => (
                <BusinessUnitTagMeasure key={id} name={name} />
              ))}
              {Array.from(
                { length: Math.max(0, value.length - 1) },
                (_, index) => (
                  <BusinessUnitOverflowChip
                    key={`overflow-measure-${index + 1}`}
                    hiddenCount={index + 1}
                    hiddenNames={[]}
                    measureOverflow
                  />
                ),
              )}
            </Box>
            <TagsInput.Context>
              {(tagsInput) => (
                <>
                  {value.slice(0, visibleTagCount).map((businessUnit) => {
                    const tagIndex = tagsInput.value.indexOf(businessUnit.name);

                    if (tagIndex === -1) {
                      return null;
                    }

                    return (
                      <TagsInput.Item
                        key={businessUnit.id}
                        index={tagIndex}
                        value={businessUnit.name}
                        className={ITEM_CLASS_NAME}
                        flexShrink={0}
                        maxW={`${BUSINESS_UNIT_TAG_MAX_WIDTH_PX}px`}
                        minW={0}
                      >
                        <TagsInput.ItemPreview
                          gap={1}
                          bg="bg.neutral.subtle"
                          borderColor="border.neutral.default"
                          borderRadius="xs"
                          px={1}
                          maxW="full"
                          minW={0}
                        >
                          <TagsInput.ItemText
                            fontSize="xs"
                            fontWeight="medium"
                            truncate
                            maxW="full"
                            title={businessUnit.name}
                          >
                            {businessUnit.name}
                          </TagsInput.ItemText>
                          <TagsInput.ItemDeleteTrigger>
                            <Cancel01Icon size={12} />
                          </TagsInput.ItemDeleteTrigger>
                        </TagsInput.ItemPreview>
                        <TagsInput.ItemInput />
                      </TagsInput.Item>
                    );
                  })}
                  {hiddenTagCount > 0 ? (
                    <BusinessUnitOverflowChip
                      hiddenCount={hiddenTagCount}
                      hiddenNames={hiddenBusinessUnitNames}
                    />
                  ) : null}
                </>
              )}
            </TagsInput.Context>
            <TagsInput.Input
              ref={inputRef}
              placeholder={value.length === 0 ? placeholder : undefined}
              fontSize="sm"
              onChange={(event) => setSearchQuery(event.target.value)}
              value={searchQuery}
              flex="1"
              minW={`${inputReservedWidthPx}px`}
              h="full"
            />
          </Box>
        </TagsInput.Control>
        <TagsInput.HiddenInput />
      </TagsInput.Root>
      <MenuWithItems
        className={MENU_CLASS_NAME}
        items={menuItems}
        positioning={{ placement: 'bottom-start', strategy: 'fixed' }}
        trigger={<Box w="full" />}
        open={isMenuOpen}
        selectionIndicator="checkbox"
        closeOnSelect={false}
        onExposeMenuApi={(contextApi: MenuContextApi) => {
          menuApiRef.current = contextApi;
        }}
        {...(searchQuery.trim().length > 0 && {
          footer: (
            <Button
              variant="ghost"
              display="block"
              w="full"
              justifyContent="flex-start"
              fontWeight="normal"
              truncate
              disabled={isExistingName || isInteractionDisabled}
              onClick={() =>
                void createMissingBusinessUnits({
                  names: [searchQuery],
                  selected: value,
                })
              }
            >
              {t('createNamed', { name: searchQuery })}
            </Button>
          ),
          footerStyles: { padding: '4px' },
        })}
      />
    </>
  );
};
